<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import Dialog from 'primevue/dialog';
import TreeTable from 'primevue/treetable';
import { TreeNode } from 'primevue/treenode';
import { useFlattenOrganizations } from '@/composables';
import { OrganizationsInterface } from '@/types';
import { EMIT } from '@/enum';

const emit = defineEmits<{
  (e: EMIT.CANCEL): void;
  (e: EMIT.SAVE, items: number[]): void;
}>();

interface AddItemsInterface {
  name: string;
  value: number;
}

interface PropsInterface {
  items?: OrganizationsInterface[] | [];
  title: string;
  className?: string | null;
  multiSelection: boolean;
  listHeaderText?: string;
  cancel?: () => void;
  save?: (items: AddItemsInterface[]) => void;
  searchable?: boolean;
  searchProperty?: string;
  loading?: boolean;
  selectedItemsData?: Record<number, Record<string, boolean>>;
}

const props = defineProps<PropsInterface>();
const { items, title, selectedItemsData } = toRefs(props);
const selectedItems = ref<Record<number, Record<string, boolean>>>(selectedItemsData?.value ?? {});
const modalVisible = ref(true);
const listHeaderText = props.listHeaderText || 'Name';
const flatOrgs = useFlattenOrganizations(items?.value as TreeNode[]);

const selectedOrganizations = computed(() => {
  return Object.keys(selectedItems.value).map((organizationId) => Number(organizationId));
});

const selectionMode = computed(() => {
  return props.multiSelection ? 'checkbox' : 'single';
});

const save = () => {
  emit(EMIT.SAVE, selectedOrganizations.value);

  selectedItems.value = {};
  modalVisible.value = false;
  return;
};

const cancel = () => {
  emit(EMIT.CANCEL);
  selectedItems.value = {};
  modalVisible.value = false;
  return;
};

const onSearch = () => {
  selectedItems.value = {};
};

/*
  The tree checkboxes behave in a way that does not match the behavior we desire. To skirt around
  these issues, this watch statement:
  #1 prevents things being checked after something has been removed.
    There should never be a case where something is unchecked and something is also added.
  #2 prevents the deselection of a parent checkbox that has no children selected
*/
watch(selectedItems, (newVal, oldVal) => {
  if (!selectionMode.value) {
    for (const [key, value] of Object.entries(newVal)) {
      if (!value.checked && !value.partialChecked) {
        if (oldVal[Number(key)]?.partialChecked) {
          delete newVal[Number(key)];
        }
      }
    }
    const newSelections = Object.keys(newVal);
    const oldSelections = Object.keys(oldVal);
    const selectionsRemoved = oldSelections.filter((selection) => !newSelections.includes(selection));
    const selectionsAdded = newSelections.filter((selection) => !oldSelections.includes(selection));
    // #1
    if (selectionsRemoved.length && selectionsAdded.length) {
      selectionsAdded.forEach((selection) => {
        delete selectedItems.value[Number(selection)];
      });
    }
    // #2
    if (selectionsRemoved.length > 1) {
      selectionsRemoved.forEach((selection) => {
        if (oldVal[Number(selection)].partialChecked) {
          selectedItems.value[Number(selection)] = { checked: true, partialChecked: false };
        }
      });
    }
  }
});

const checkSelectedItems = (data: TreeNode) => {
  let orgData = flatOrgs.find((org) => org?.id === data?.key);
  if (!orgData?.parent_id) return;
  let hasParent = true;
  while (hasParent) {
    orgData = flatOrgs.find((org) => org?.id === orgData?.parent_id);

    if (orgData?.id) delete selectedItems.value[Number(orgData?.id)];
    hasParent = orgData?.parent_id ? true : false;
  }
};

const filters = ref({ name: '' });
</script>

<template>
  <Dialog v-model:visible="modalVisible" class="dialog__container" :modal="true" :draggable="false" @hide="cancel">
    <template #header>
      <div class="dialog__header flex flex-column w-8">
        <h2 class="mb-3" v-text="props.title" />
        <IconField icon-position="left">
          <InputIcon>
            <Icon name="search" class="text-lg" />
          </InputIcon>
          <InputText v-model="filters.name" class="w-full" placeholder="Search Organizations" @input="onSearch" />
        </IconField>
      </div>
    </template>
    <TreeTable
      v-model:filters="filters"
      v-model:selectionKeys="selectedItems"
      :value="items"
      :meta-key-selection="!multiSelection"
      :global-filter-fields="['name']"
      :scrollable="true"
      :selection-mode="selectionMode"
      responsive-layout="scroll"
      scroll-height="flex"
      @node-select="checkSelectedItems"
    >
      <template v-if="props.searchable">
        <Column :expander="true" field="name" :header="listHeaderText" sortable />
      </template>
      <template #empty>No organization to be added</template>
    </TreeTable>
    <template #footer>
      <AppButton type="secondary" text="Cancel" @click="cancel" />
      <AppButton type="primary" :text="title" :disabled="!Object.keys(selectedItems).length" @click="save" />
    </template>
  </Dialog>
</template>
